'use client';

import { useLocale, useTranslations } from 'next-intl';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import {
    HiddenSections,
    HomeEntertainmentOffer,
    PageLocale
} from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import { isSectionHidden } from '@/utils/landing-page/page-layout';

/** DOM id of the home-entertainment section — the in-page jump target of the hero CTA. */
export const HOME_ENTERTAINMENT_ANCHOR_ID = 'available-at';

type HomeEntertainment = {
    /**
     * Whether the page runs in home-entertainment mode, i.e. the showtimes CTA, the showtimes
     * finder and the menu's "Tickets" entry are replaced. Active as soon as there is at least
     * one offer for the CTA to jump to, and the section is not hidden.
     */
    isActive: boolean;
    /** Localized headline of the offers section. */
    headline: string;
    /** Localized label of the hero button and the menu entry. */
    buttonLabel: string;
    offers: HomeEntertainmentOffer[];
};

export function useHomeEntertainment(): HomeEntertainment {
    const t = useTranslations();
    const locale = useLocale() as PageLocale;
    const { domainConfiguration } = useDomainConfiguration();

    // A hidden section is not on the page, so the hero CTA must not jump to it.
    const isHidden = isSectionHidden(domainConfiguration, HiddenSections.HOME_ENTERTAINMENT);
    const offers = domainConfiguration.homeEntertainment?.offers ?? [];
    const headline = domainConfiguration.homeEntertainment?.headline?.[locale] || t('home-entertainment.title');

    return {
        isActive: !isHidden && offers.length > 0,
        headline: headline,
        buttonLabel: domainConfiguration.homeEntertainment?.buttonText?.[locale] || headline,
        offers: offers
    };
}
